import "./index.css";

import { useContext } from "react";
import { useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";

import { BulkUploadButton } from "./components/bulk-upload-modal/bulk-upload-button.tsx";
import { CarrierRatesSummary } from "./components/carrier-selection/components/carrier-rates-summary/carrier-rates-summary.tsx";
import { CarrierSelectionForm } from "./components/carrier-selection/index.tsx";
import {
	CompletedHeader,
	CompletedHeaderType,
} from "./components/completed-header/completed-header.tsx";
import { QuoteDetailsSummary } from "./components/quote-details/components/quote-details-summary/quote-details-summary.tsx";
import { QuoteInfoButton } from "./components/quote-details/components/quote-info-modal/quote-info-button.tsx";
import { QuoteDetailsForm } from "./components/quote-details/index.tsx";
import type { Step } from "./components/quoting-accordion/quoting-accordion.tsx";
import { QuotingAccordion } from "./components/quoting-accordion/quoting-accordion.tsx";
import { ShipmentDetailsForm } from "./components/shipment-details/index.tsx";
import {
	QuoteDetailsContext,
	QuoteDetailsProvider,
} from "./context/quote-details-context.tsx";
import {
	useCustomerDataFetch,
	useScrollToTop,
} from "./hooks/use-quote-hooks.ts";
import { useStepLogic } from "./hooks/use-step-logic.ts";

export function Quote() {
	const { quoteState, setCustomerDetails, setCreditDetails } =
		useContext(QuoteDetailsContext);
	const navigate = useNavigate();
	const { isInternal } = useDeploymentType();
	const {
		currentStep,
		setCurrentStep,
		selectedCarrier,
		disableGetQuoteButton,
		getStepCompleted,
	} = useStepLogic(quoteState);

	useCustomerDataFetch(
		quoteState.customer?.id,
		setCustomerDetails,
		setCreditDetails,
		isInternal,
	);
	useScrollToTop(currentStep);

	const stepTemplates: Step[] = [
		{
			step: 1,
			id: "shipment-information",
			title: "Quote Details",
			header: (
				<div className={"step-header-actions"}>
					<QuoteInfoButton />
					<BulkUploadButton />
				</div>
			),
			completedHeader: (
				<CompletedHeader
					onClick={() => {
						if (quoteState.id) {
							navigate(
								routes.quoteDetailsWithId.replace(
									":quoteId",
									quoteState.id.toString(),
								),
							);
						} else {
							navigate(routes.quoteDetails);
						}
					}}
					quoteState={quoteState}
					type={CompletedHeaderType.QuoteDetails}
				/>
			),
			completedContent: <QuoteDetailsSummary />,
			active: false,
			completed: false,
			content: undefined,
		},
		{
			step: 2,
			id: "carrier-rates",
			title: "Carrier Rates",
			completedHeader: (
				<CompletedHeader
					onClick={() => {
						if (quoteState.id) {
							navigate(
								routes.carrierSelection.replace(
									":quoteId",
									quoteState.id.toString(),
								),
							);
						} else {
							navigate(routes.quoteDetails);
						}
					}}
					quoteState={quoteState}
					selectedCarrier={selectedCarrier}
					type={CompletedHeaderType.CarrierRates}
				/>
			),
			completedContent: <CarrierRatesSummary />,
			active: false,
			completed: false,
			content: undefined,
		},
		{
			step: 3,
			id: "shipment-details",
			title: "Shipment Details",
			completedHeader: (
				<CompletedHeader
					onClick={() => {
						if (quoteState.id) {
							navigate(
								routes.shipmentDetails.replace(
									":quoteId",
									quoteState.id.toString(),
								),
							);
						} else {
							navigate(routes.quoteDetails);
						}
					}}
					selectedCarrier={selectedCarrier}
					type={CompletedHeaderType.ShipmentDetails}
				/>
			),
			completedContent: <div></div>,
			active: false,
			completed: false,
			content: undefined,
		},
	];

	const steps: Step[] = stepTemplates.map((template) => ({
		...template,
		active: template.step === currentStep,
		completed: getStepCompleted(template.step),
		content: (() => {
			switch (template.id) {
				case "shipment-information": {
					return (
						<QuoteDetailsForm
							disableSubmitButton={disableGetQuoteButton}
							onStepChange={() => {
								setCurrentStep(1);
							}}
						/>
					);
				}
				case "carrier-rates": {
					return <CarrierSelectionForm />;
				}
				case "shipment-details": {
					return <ShipmentDetailsForm />;
				}
				default: {
					return null;
				}
			}
		})(),
	}));

	return (
		<div id={"quote-wrapper"}>
			<TraxPageHeader
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Create LTL Quote"}
			/>
			<QuotingAccordion steps={steps} />
		</div>
	);
}

export function QuoteDetails() {
	return (
		<QuoteDetailsProvider>
			<Quote />
		</QuoteDetailsProvider>
	);
}
